/**
 * Session Bridge utility for cross-origin session sharing between
 * React TNEMIS and Angular TNEMIS via hidden iframe + postMessage.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 8.2, 8.3
 */

const OAUTH_ENDPOINT =
  'https://eeqzof4a8g.execute-api.ap-south-1.amazonaws.com/api/oauth_test';

const getAngularOrigin = (): string =>
  (process.env.REACT_APP_API_environment === 'LOCAL'
    ? 'http://localhost:4200'
    : process.env.REACT_APP_API_environment === 'STAGING'
      ? 'https://tnemis-staging.tnsed.com'
      : 'https://tnemis.tnschools.gov.in'
  ).replace(/\/$/, '');

/**
 * Ensures AuthorizationKey is present in localStorage before session priming.
 * If missing, fetches it from the OAuth endpoint and stores it.
 * If the fetch fails, proceeds silently (don't block priming).
 *
 * Requirements: 8.2, 8.3
 */
async function ensureAuthorizationKey(): Promise<void> {
  const existingKey = localStorage.getItem('AuthorizationKey');
  if (existingKey) return;

  try {
    const response = await fetch(OAUTH_ENDPOINT);
    if (response.ok) {
      const data = await response.json();
      if (data.Authorization_key) {
        localStorage.setItem('AuthorizationKey', data.Authorization_key);
      }
    }
  } catch {
    // OAuth fetch failed — proceed without blocking priming
  }
}

/**
 * Primes the Angular TNEMIS session by posting all localStorage data
 * to the Angular receiver.html via a hidden iframe bridge.
 *
 * - Ensures AuthorizationKey is present in localStorage (fetches from OAuth if missing)
 * - Creates a hidden iframe targeting `${angularOrigin}/receiver.html`
 * - On iframe load: posts JSON.stringify of all localStorage key-value pairs
 * - Resolves with 'success' when Angular acknowledges receipt
 * - Resolves with 'timeout' after 5 seconds if no acknowledgment
 * - Resolves with 'error' if the iframe fails to load
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 8.2, 8.3
 */
export async function primeAngularSession(): Promise<
  'success' | 'timeout' | 'error'
> {
  // Ensure AuthorizationKey exists before priming (Requirements: 8.2, 8.3)
  await ensureAuthorizationKey();

  return new Promise((resolve) => {
    const angularOrigin = getAngularOrigin();
    const iframe = document.createElement('iframe');
    iframe.src = `${angularOrigin}/receiver.html`;
    iframe.style.display = 'none';

    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      window.removeEventListener('message', onMessage);
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== angularOrigin) return;
      if (event.data === 'success' && !settled) {
        settled = true;
        cleanup();
        resolve('success');
      }
    };

    iframe.onload = () => {
      if (settled) return;
      // Build payload from all localStorage key-value pairs
      const payload: Record<string, string> = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          const value = localStorage.getItem(key);
          if (value !== null) {
            payload[key] = value;
          }
        }
      }
      iframe.contentWindow?.postMessage(JSON.stringify(payload), angularOrigin);
    };

    iframe.onerror = () => {
      if (!settled) {
        settled = true;
        cleanup();
        resolve('error');
      }
    };

    window.addEventListener('message', onMessage);
    document.body.appendChild(iframe);

    // 5-second timeout
    timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true;
        cleanup();
        resolve('timeout');
      }
    }, 5000);
  });
}

/**
 * Clears the Angular TNEMIS session by sending a COMMAND_CLEAR_STORAGE
 * message to the Angular receiver.html via a hidden iframe bridge.
 *
 * - Creates a hidden iframe targeting `${angularOrigin}/receiver.html`
 * - On iframe load: posts 'COMMAND_CLEAR_STORAGE' via postMessage
 * - Resolves with 'clear_success' when Angular acknowledges
 * - Resolves with 'timeout' after 1 second if no acknowledgment
 */
export function clearAngularSession(): Promise<'clear_success' | 'timeout'> {
  return new Promise((resolve) => {
    const angularOrigin = getAngularOrigin();

    // Reuse existing iframe if present, otherwise create new one
    let iframe = document.querySelector<HTMLIFrameElement>(
      'iframe[src*="/receiver.html"]'
    );
    const isExisting = !!iframe;

    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.src = `${angularOrigin}/receiver.html`;
      iframe.style.display = 'none';
    }

    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      window.removeEventListener('message', onMessage);
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== angularOrigin) return;
      if (event.data === 'clear_success' && !settled) {
        settled = true;
        cleanup();
        resolve('clear_success');
      }
    };

    const sendClearCommand = () => {
      if (settled) return;
      iframe!.contentWindow?.postMessage(
        'COMMAND_CLEAR_STORAGE',
        angularOrigin
      );
    };

    window.addEventListener('message', onMessage);

    if (isExisting) {
      // Iframe already loaded, send command immediately
      sendClearCommand();
    } else {
      iframe.onload = sendClearCommand;
      document.body.appendChild(iframe);
    }

    // 1-second timeout
    timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true;
        cleanup();
        resolve('timeout');
      }
    }, 1000);
  });
}
